/**
 * Step building blocks shared by every Observations tour
 * (``list.ts``, ``detail.ts``, ``combined.ts``).
 *
 * Mirrors the punch-lists structure, with two differences:
 *   - The Observations detail page has an optional Safety / Quality card
 *     that only renders when the observation has hazard / contributing
 *     behavior / contributing condition fields populated. The
 *     ``safetyStep`` below is ``skipIf``-gated on its presence so the
 *     tour skips it for routine observations.
 *   - The list-side new-item button targets a different selector and
 *     copy is tailored to "observations" rather than "punch list items".
 */
import type { TourStep } from '../types';
import { clickPreferReact, has, waitForCondition } from '../utils/dom';

// ---------------------------------------------------------------------------
// Selectors / predicates
// ---------------------------------------------------------------------------

/** True once the observation detail page is mounted in the DOM. */
export const hasDetailMounted = (): boolean =>
  has('[data-tour-id="observation-detail-header-actions"]');

const hasSafetyCard = (): boolean =>
  has('[data-tour-id="observation-detail-safety"]');

// ---------------------------------------------------------------------------
// List-side steps
// ---------------------------------------------------------------------------

export const newObservationStep: TourStep = {
  id: 'new-observation',
  target: '[data-tour-id="observations-new"]',
  placement: 'left',
  title: 'Log a new observation',
  body:
    'Click here to capture a new safety, quality, or site-condition ' +
    'observation.',
};

export const statusChartStep: TourStep = {
  id: 'status-chart',
  target: '[data-tour-id="observations-status-chart"]',
  placement: 'right',
  title: 'Status at a glance',
  body:
    'See how observations break down across open, completed, and overdue ' +
    'states without scrolling through the table.',
};

export const filtersStep: TourStep = {
  id: 'filters',
  target: '[data-tour-id="observations-filters"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Find what you need',
  body:
    'Search by keyword or filter by status and priority to narrow the ' +
    'list down.',
};

/** "Open an observation" — list-only flavor (description, no row click). */
export const listTableStep: TourStep = {
  id: 'table',
  target: '[data-tour-id="observations-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open an observation',
  body: 'Each row is one observation. Click any row to open the full details.',
};

/**
 * "Open an observation" — combined flavor: clicking Next opens the first
 * row and waits for the detail page to mount before letting the tour
 * advance into the detail steps.
 */
export const combinedTableStep: TourStep = {
  id: 'open-an-observation',
  target: '[data-tour-id="observations-table"]',
  placement: 'top',
  spotlightPadding: 4,
  title: 'Open an observation',
  body:
    'Click any row to open it. Press Next and we\u2019ll open the first ' +
    'one for you.',
  onBeforeNext: async () => {
    clickPreferReact('[data-tour-id="observations-table"] tbody tr');
    await waitForCondition(hasDetailMounted, 4000, 'row click → detail page');
  },
  autoAdvanceWhen: () => hasDetailMounted(),
};

// ---------------------------------------------------------------------------
// Detail-side steps
// ---------------------------------------------------------------------------

export const headerActionsStep: TourStep = {
  id: 'header-actions',
  target: '[data-tour-id="observation-detail-header-actions"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Status & actions',
  body:
    'Update the observation\u2019s status, remove it, or jump straight ' +
    'to its 360\u00B0 hotspot if it has one.',
};

export const metadataStep: TourStep = {
  id: 'metadata',
  target: '[data-tour-id="observation-detail-metadata"]',
  placement: 'bottom',
  spotlightPadding: 4,
  title: 'Key fields',
  body:
    'A quick summary of the most important fields — priority, due date, ' +
    'assignee, type, location, trade, and spec section.',
};

export const descriptionStep: TourStep = {
  id: 'description',
  target: '[data-tour-id="observation-detail-description"]',
  placement: 'right',
  spotlightPadding: 4,
  title: 'Description',
  body: 'The full write-up of what was observed.',
};

export const attachmentsStep: TourStep = {
  id: 'attachments',
  target: '[data-tour-id="observation-detail-attachments"]',
  placement: 'right',
  spotlightPadding: 4,
  title: 'Attachments',
  body:
    'Upload photos, drawings, or any supporting files. Image attachments ' +
    'show up in a gallery; everything else lives in a downloads list.',
};

export const peopleStep: TourStep = {
  id: 'people',
  target: '[data-tour-id="observation-detail-people"]',
  placement: 'left',
  spotlightPadding: 4,
  title: 'People',
  body:
    'Who\u2019s involved — assignee, creator, notification date, and ' +
    'anyone on the distribution list.',
};

/**
 * Safety / quality details — hazard + contributing factors.
 *
 * The card on the detail page only renders when the observation has any
 * of those three fields populated; we ``skipIf`` accordingly so the tour
 * silently moves past it for routine observations that don't need a
 * safety call-out.
 */
export const safetyStep: TourStep = {
  id: 'safety',
  target: '[data-tour-id="observation-detail-safety"]',
  placement: 'left',
  spotlightPadding: 4,
  title: 'Safety / quality',
  body:
    'When an observation captures a hazard or quality concern, the ' +
    'contributing behaviors and conditions live here.',
  skipIf: () => !hasSafetyCard(),
};
